// EntitlementsContext — resolves an org's entitlements LOCALLY from the
// entitlement engine (no network call), once per billingDetails (PE-1572;
// the shape PE-1624 formalizes for dashboard-wide gating).
//
// WHY LOCAL: `merchant.billingDetails` already arrives with the merchant (from
// GET /merchant, flattened via flatten_v2_to_v1_compat). The engine
// (`@charitystack/entitlements`) is a pure in-process function over that state
// — so `can()` / `limit()` / `fees()` are derived, never fetched, and never
// re-derived by hand (the catalog/grid/fee numbers live ONLY in the engine).
//
// SERVER IS STILL AUTHORITY: FE gating is proactive UX only. The owning backend
// re-checks entitlement on every privileged call; this provider must never be
// the sole gate (an org could hit the API directly).
//
// FAIL POLICY: the engine FAILS LOUD — `resolve()` throws on an unknown plan or
// on a fee it can't derive. A React provider must NOT crash the whole dashboard on
// that, so resolution is wrapped in try/catch. But the response is NOT uniform —
// it splits on the AXIS the failure is in (the engine's own separation of concerns):
//
//   • FEES-ONLY failure (MissingFeeOverrideError / MissingNegotiatedFeeError) — the
//     plan and feature grid resolved fine; only the fee number couldn't be derived
//     (e.g. a legacy_custom org whose negotiated feeAmounts live at the TOP LEVEL of
//     the org record, which the engine never receives — it only sees billingDetails;
//     PE-1700 read-path gap). A missing FEE must NEVER strip an org's FEATURES: that
//     is the axis-separation rule the engine documents (is_entitled/getLimit compute
//     features/limits WITHOUT fees for exactly this reason). So we recompute
//     features/limits via those fee-independent APIs, keep `isLegacyCustom`, and only
//     null out `fees()`. NOT degraded — the managed billing view renders (it shows
//     "Custom pricing" text, never a numeric fee, so it needs no fee value anyway).
//
//   • Any OTHER failure (unknown plan/override, i.e. a genuinely corrupt record) —
//     degrade to the conservative DENY posture (no features, null limits, no fees)
//     with a console error, so a corrupt billing record can't white-screen the app or
//     silently grant access.

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  resolve,
  isEntitled,
  getLimit,
  CATALOG,
  PLAN_TO_TIER,
  isLegacyCustom as isLegacyCustomBilling,
  setLegacySafeDefault,
  MissingFeeOverrideError,
  MissingNegotiatedFeeError,
  type OrgBillingDetails,
  type Plan,
  type Tier,
  type LimitValue,
} from '@charitystack/entitlements';

// Dev-only escape hatch for testing the PE-1620 post-flip entitlement behavior.
// Set VITE_ENTITLEMENTS_LEGACY_SAFE=false in the dev server environment to call
// setLegacySafeDefault(false) at app init. Never set this to false in production.
if (import.meta.env.VITE_ENTITLEMENTS_LEGACY_SAFE === 'false') {
  setLegacySafeDefault(false);
}

export type EntitlementsContextValue = {
  // can(featureKey) — true iff the org is entitled to the feature NOW.
  // Unknown/roadmap/none features → false.
  can: (feature: string) => boolean;
  // limit(limitKey) — the org's usage limit for a key at its tier:
  // a number, 'unlimited', or null (not available on this tier / unknown key).
  limit: (key: string) => LimitValue;
  // fees() — the org's derived fee rates as exact decimal strings
  // ({ platformPct, processingPct }), or null if fees can't be derived
  // (e.g. a negotiated tier missing its feeOverride — fail-loud degraded).
  fees: () => { platformPct: string; processingPct: string } | null;
  // The org's resolved plan + tier (plan is the stored slug; tier is the
  // entitlement bucket). Useful for the billing page's "current plan" state.
  plan: Plan;
  tier: Tier;
  // The raw catalog (prices, full grid). The billing page reads
  // subscriptionPrices + the feature grid from here — NEVER a hand-rolled copy.
  catalog: typeof CATALOG;
  // True when resolution failed and we degraded to the deny posture. Lets a
  // consumer surface a "couldn't load your plan" state instead of silently
  // showing an empty grid.
  degraded: boolean;
  // True iff the org is a managed (legacy_custom) account — either it carries the
  // `legacy_custom` override profile, OR it carries the legacy `plan: 'CUSTOM'`
  // sentinel (the pre-override shape of the 16 negotiated prod orgs; see
  // isLegacyCustomPlanSentinel). A negotiated, manually-provisioned account. Drives
  // the PE-1678 treatment: hide the self-serve billing screen + show "Contact us"
  // instead of a self-serve upgrade CTA. This is a PROFILE property, not a feature
  // (legacy_custom and grandfathered_base share features; only legacy_custom is
  // managed), so it is surfaced explicitly here rather than inferred from can()/limit().
  //
  // DEGRADED ⇒ FALSE: when resolution degraded we can't trust the billing record,
  // so we fall back to the STANDARD experience (show billing) rather than
  // suppressing it off an unreliable signal. (A recognized 'CUSTOM' sentinel resolves
  // on the SUCCESS path — it is not degraded — so this invariant is untouched.)
  isLegacyCustom: boolean;
  // True once the merchant prop has actually loaded. Until then, entitlements
  // resolve to implicit STARTER (absent billingDetails) — which is INDISTINGUISH-
  // ABLE from a real Starter org. Route guards MUST consult this before locking,
  // or a hard refresh onto a gated route locks out an entitled org (and, because
  // each page fetches the merchant itself, gating out the page prevents the fetch
  // and the lock never recovers). See EntitlementGate in App.js.
  ready: boolean;
};

const EntitlementsContext = createContext<EntitlementsContextValue | null>(null);

// Coalesce the dashboard's loosely-typed merchant.billingDetails into the
// engine's OrgBillingDetails. The fields line up 1:1; this is a typed narrowing,
// not a transform. An absent/garbage billingDetails resolves to implicit
// STARTER inside the engine (sparse `plan` ⇒ STARTER, §6).
function toBillingDetails(billingDetails: unknown): OrgBillingDetails {
  if (!billingDetails || typeof billingDetails !== 'object') {
    return {};
  }
  // The engine only reads plan / entitlementOverrides / coveredCommitment /
  // feeOverride (+ ignores processingFees). Pass the object through; the engine
  // validates and coalesces. Cast is safe: the engine treats unexpected values
  // as data-integrity errors (fail-loud), which we catch below.
  return billingDetails as OrgBillingDetails;
}

// The 16 negotiated prod orgs carry `billingDetails.plan == 'CUSTOM'` — a legacy
// misuse of `plan` as the fee-deal marker, predating the entitlementOverrides model
// (prod scan 2026-06-30). The engine has no 'CUSTOM' plan and fails loud on it
// (UnknownPlanError), which the provider below would otherwise degrade to the deny
// posture — surfacing the "couldn't load your billing details" error card instead of
// the managed "Custom" view these orgs should see.
//
// This mirrors the backend backfill discriminator (`_is_custom_marker` in
// accounts-backend/scripts/backfill_entitlement_overrides.py): the literal 'CUSTOM'
// value (case-insensitive, trimmed) IS the legacy_custom signal. The normalization
// MUST match the backend exactly, or a value like ' Custom ' would be stamped
// legacy_custom by the backfill yet degrade here.
//
// Deliberately narrow: ONLY the exact 'CUSTOM' sentinel. A genuinely-unknown plan
// ('NONSENSE_PLAN', 'FREE', …) is a real data-integrity bug and must still degrade
// (fail-loud preserved).
function isLegacyCustomPlanSentinel(plan: unknown): boolean {
  return typeof plan === 'string' && plan.trim().toUpperCase() === 'CUSTOM';
}

const DENY_VALUE: Omit<
  EntitlementsContextValue,
  'plan' | 'tier' | 'catalog' | 'degraded' | 'isLegacyCustom' | 'ready'
> = {
  can: () => false,
  limit: () => null,
  fees: () => null,
};

export function EntitlementsProvider({
  merchant,
  children,
}: {
  // The merchant object the dashboard prop-drills (App.js useState). Only
  // `billingDetails` is read; typed loosely to avoid coupling to the full
  // merchant shape (which the dashboard does not strongly type).
  merchant: { billingDetails?: unknown } | null | undefined;
  children: ReactNode;
}) {
  // Has the merchant prop hydrated yet? `merchant` is null until GET /merchant
  // resolves. Computed outside the memo so it can be a dependency (it flips
  // null → object even when billingDetails stays absent).
  const ready = merchant != null && typeof merchant === 'object';

  const value = useMemo<EntitlementsContextValue>(() => {
    const billing = toBillingDetails(merchant?.billingDetails);

    // Legacy 'CUSTOM' plan sentinel (see isLegacyCustomPlanSentinel). These orgs are
    // managed/negotiated accounts that the engine can't resolve directly. Coerce ONLY
    // the plan field to STARTER so resolve() succeeds, leaving entitlementOverrides
    // and everything else untouched: a backfilled org (override='legacy_custom') gets
    // its genuine legacy bundle unioned on top; an un-backfilled org gets the
    // legacy-safe-default bundle (prod default ON). Either way the feature set matches
    // legacy_custom, and forcing isLegacyCustom below routes it to the managed view.
    const forceLegacyCustom = isLegacyCustomPlanSentinel(billing.plan);
    const billingForResolve: OrgBillingDetails = forceLegacyCustom
      ? { ...billing, plan: 'STARTER' }
      : billing;

    try {
      const resolved = resolve(billingForResolve);
      // resolve() validated the plan; recompute the plan/tier for exposure.
      // An absent plan means implicit STARTER (engine §6) — mirror that here so
      // `plan`/`tier` are never undefined for consumers.
      const plan: Plan = (billingForResolve.plan ?? 'STARTER') as Plan;
      const tier: Tier = PLAN_TO_TIER[plan] ?? 'starter';

      return {
        can: (feature: string) => resolved.features.has(feature),
        limit: (key: string) => (key in resolved.limits ? resolved.limits[key] : null),
        fees: () => resolved.fees,
        plan,
        tier,
        catalog: CATALOG,
        degraded: false,
        // A recognized 'CUSTOM' sentinel IS a managed (legacy_custom) account by the
        // backend's own definition, so force the managed view regardless of whether
        // the override has been backfilled yet. Otherwise fall back to the real
        // override check on the untouched billing record.
        isLegacyCustom: forceLegacyCustom || isLegacyCustomBilling(billing),
        ready,
      };
    } catch (err) {
      // Any OTHER failure (unknown plan/override — a genuinely corrupt record).
      // Degrade to deny rather than crash the dashboard. Surface for observability.
      // Also the fallthrough for an UNexpected throw from the fees-only path below.
      const degradeToDeny = (cause: unknown): EntitlementsContextValue => {
        // eslint-disable-next-line no-console
        console.error(
          '[EntitlementsProvider] failed to resolve entitlements; degrading to deny posture',
          cause,
        );
        return {
          ...DENY_VALUE,
          plan: 'STARTER' as Plan,
          tier: 'starter' as Tier,
          catalog: CATALOG,
          degraded: true,
          // Degraded ⇒ not trusted ⇒ standard experience (see field doc above).
          isLegacyCustom: false,
          ready,
        };
      };

      // FEES-ONLY failure: the plan/feature axis is fine; only the fee number
      // couldn't be derived (a negotiated org whose feeAmounts the engine can't see —
      // PE-1700 read-path gap). A missing fee must never strip features (axis
      // separation), so recompute features/limits via the fee-INDEPENDENT engine APIs
      // (isEntitled/getLimit — the same paths the engine exposes precisely so a fee
      // gap can't block an entitlement check) and only null out fees(). Not degraded.
      if (err instanceof MissingFeeOverrideError || err instanceof MissingNegotiatedFeeError) {
        try {
          const plan: Plan = (billingForResolve.plan ?? 'STARTER') as Plan;
          const tier: Tier = PLAN_TO_TIER[plan] ?? 'starter';

          // Compute features/limits EAGERLY here (inside the memo + this guard), then
          // close over the results — so can()/limit() are pure lookups that never
          // throw at call-time, mirroring the success path (where they close over
          // resolved.features/resolved.limits). Calling isEntitled/getLimit lazily in
          // the closures would run engine code AFTER useMemo returned, outside any
          // try/catch — a surprise throw there would escape uncaught to the consumer.
          const features = new Set<string>(
            [...CATALOG.allLiveFeatureKeys].filter((f) => isEntitled(billingForResolve, f)),
          );
          const limits: Record<string, LimitValue> = {};
          for (const key of Object.keys(CATALOG.limits)) {
            limits[key] = getLimit(billingForResolve, key);
          }

          // Observability: this branch is expected for negotiated orgs, but log so
          // operators can see it fire (and catch it firing for an UNexpected shape).
          // eslint-disable-next-line no-console
          console.warn(
            '[EntitlementsProvider] fees-only failure (features/limits intact, fees null); likely a negotiated org whose feeAmounts the engine cannot see (PE-1700)',
            err,
          );

          return {
            can: (feature: string) => features.has(feature),
            limit: (key: string) => (key in limits ? limits[key] : null),
            fees: () => null,
            plan,
            tier,
            catalog: CATALOG,
            degraded: false,
            isLegacyCustom: forceLegacyCustom || isLegacyCustomBilling(billing),
            ready,
          };
        } catch (feErr) {
          // The fee-independent APIs threw unexpectedly (should not happen today —
          // features/limits never compute fees). Degrade rather than escape uncaught.
          return degradeToDeny(feErr);
        }
      }

      return degradeToDeny(err);
    }
  }, [merchant?.billingDetails, ready]);

  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
}

// Hook for consumers. Throws if used outside the provider — a wiring bug, not a
// runtime-data condition, so failing loud here is correct.
export function useEntitlements(): EntitlementsContextValue {
  const ctx = useContext(EntitlementsContext);
  if (!ctx) {
    throw new Error('useEntitlements must be used within an EntitlementsProvider');
  }
  return ctx;
}
